import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, onMounted } from 'vue';
import BaseEditableCard from '@/components-UI/BaseEditableCard.vue';
import BaseTable from '@/components-UI/table/BaseTable.vue';
import BaseButton from '@/components-UI/BaseButton.vue';
import BaseInput from '@/components-UI/BaseInput.vue';
import BaseInputNumber from '@/components-UI/BaseInputNumber.vue';
import BaseDateTimePicker from '@/components-UI/BaseDateTimePicker.vue';
import BaseQuestionMarkTooltip from '@/components-UI/BaseQuestionMarkTooltip.vue';
import BaseSelect from '@/components-UI/BaseSelect.vue';
import SvgPlus from '@/assets/icons/plus.svg';
import SvgTrash from '@/assets/icons/trash.svg';
import { useEUAccountsStore } from '@/store_v3/EUAccountsStore';
import { storeToRefs } from 'pinia';
import { getPayableServiceUnits } from '@/api/revenue';
import { showServerError } from '@/utils';
import { CellsType, type RawConfig, prepareItems } from '@/utils/CellsFactory';
import { IEUAccountServiceUnit, IEUAccountServices } from '@/api/types/revenue';
import { ITableHeader } from '@/utils';
import { getCurrencyIcon } from '@/utils/translate';
import router from '@/router';
import {
    getDefaultTrafficPeriodForMonth,
    serviceToTrafficPeriodString,
    trafficPeriodStringToRange,
} from '@/views/revenue/EUAccounts/euAccountTrafficPeriod';
import { isEuAccountPercentGoal } from '@/views/revenue/EUAccounts/euAccountGoalRevenue';


export default /*#__PURE__*/_defineComponent({
  __name: 'ComponentServicesToPay',
  setup(__props) {

const EUAccountsStore = useEUAccountsStore();
const {
    form,
    isEdit,
    servicesToPay,
    getServicesToPaySelectedSum,
    getServicesToPayTargetSum,
    isServicesToPayDifferenceMoreThanOnePercent,
    isEUAccountFullyNetted,
} = storeToRefs(EUAccountsStore);
const { formatNumber, recountServicesSum } = EUAccountsStore;
const isSingle = computed(() => router.currentRoute.params.id !== 'create');

const unitOptions = ref<IEUAccountServiceUnit[]>([]);
const isUnitsLoading = ref(false);

onMounted(async () => {
    try {
        isUnitsLoading.value = true;
        unitOptions.value = await getPayableServiceUnits();
    } catch (err) {
        // Справочник не критичен для остальной страницы: сам селект останется пустым
        showServerError(err, 'Не удалось загрузить типы целей');
    } finally {
        isUnitsLoading.value = false;
    }
});

const getServiceKey = (item: IEUAccountServices, index = 0): string =>
    String(item.tempId ?? `${item.offer_id}_${item.goal_id}_${index}`);

const createUniqueTempId = (): string => `temp_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

const tableHeadersTop: ITableHeader[] = [
    { text: 'ID оффера', value: 'offer_id', width: '90px', align: 'center' },
    { text: 'Оффер', value: 'offer_name', width: 'auto' },
    { text: 'ID цели', value: 'goal_service_id', width: '90px', align: 'center' },
    { text: 'Цель', value: 'goal_name', width: 'auto' },
    { text: 'Ставка', value: 'original_amount', width: '112px', align: 'end' },
    { text: 'Курс', value: 'exchange_rate', width: '96px', align: 'end' },
    { text: 'Количество', value: 'quantity', width: '112px', align: 'center' },
    { text: 'Итого', value: 'final_amount', width: '112px', align: 'end' },
    { text: 'Действия', value: 'actions', width: '48px', align: 'center' },
];

const tableHeadersBottom: ITableHeader[] = [
    { text: 'ID оффера', value: 'offer_id', width: '90px', align: 'center' },
    { text: 'ID цели', value: 'goal_service_id', width: '90px', align: 'center' },
    { text: 'Услуга', value: 'service_name', width: 'auto' },
    { text: 'Тип цели', value: 'unit', width: '140px' },
    { text: 'Период трафика', value: 'traffic_period', width: '200px', align: 'end' },
    { text: 'Ставка', value: 'original_amount', width: '100px', align: 'end' },
    { text: 'Курс', value: 'exchange_rate', width: '108px', align: 'end' },
    { text: 'Количество', value: 'quantity', width: '112px', align: 'center' },
    { text: 'Итого', value: 'final_amount', width: '112px', align: 'end' },
    { text: 'Действия', value: 'actions', width: '48px', align: 'center' },
];

const tableConfigsTop: RawConfig<IEUAccountServices & { actions: unknown }> = {
    [CellsType.Route]: {
        offer_id: {
            name: 'offer',
            isChip: true,
            valueKey: 'offer_id',
        },
        offer_name: {
            name: 'offer',
            valueKey: 'offer_id',
            text: 'offer_name',
        },
        goal_service_id: {
            name: 'offer',
            isChip: true,
            valueKey: 'offer_id',
            text: 'goal_service_id',
        },
        goal_name: {
            name: 'offer',
            valueKey: 'offer_id',
            text: 'goal_name',
        },
    },
    [CellsType.Uniq]: {
        original_amount: {},
        exchange_rate: {},
        final_amount: {},
        actions: {},
    },
    [CellsType.Number]: {
        quantity: {
            digits: 0,
        },
    },
};

const tableConfigsBottom: RawConfig<IEUAccountServices & { actions: unknown }> = {
    [CellsType.Route]: {
        offer_id: {
            name: 'offer',
            isChip: true,
            valueKey: 'offer_id',
        },
        goal_service_id: {
            name: 'offer',
            isChip: true,
            valueKey: 'offer_id',
            text: 'goal_service_id',
        },
    },
    [CellsType.Uniq]: {
        service_name: {},
        unit: {},
        traffic_period: {},
        original_amount: {},
        exchange_rate: {},
        final_amount: {},
        actions: {},
        quantity: {},
    },
};

const tableTopItems = computed(() =>
    prepareItems(
        form.value.services ?? [],
        tableConfigsTop,
        tableHeadersTop.map((header) => header.value),
    ),
);

const tableBottomItems = computed(() =>
    prepareItems(
        servicesToPay.value,
        tableConfigsBottom,
        tableHeadersBottom.map((header) => header.value),
    ),
);
const hasSelectedServices = computed(() => servicesToPay.value.length > 0);
const isFieldsEditable = computed(() => (isSingle.value ? isEdit.value : true));
const footerRightColspan = computed(() => (isEdit.value ? 3 : 2));
const footerLeftColspan = computed(() => 10 - footerRightColspan.value);
const topTableNoDataText = computed(() =>
    (form.value.services?.length ?? 0) > 0
        ? 'Нет данных'
        : 'Сначала заполните услуги за период',
);

const isOfferCurrencyDiffers = (item: IEUAccountServices): boolean => {
    if (isEuAccountPercentGoal(item)) return false;
    return (item?.goal_currency !== form.value.currency && item?.currency !== form.value.currency) === true;
};

const getRateCurrency = (item: IEUAccountServices): string =>
    (isEuAccountPercentGoal(item) ? form.value.currency : (item.goal_currency || item.currency || form.value.currency));

const isFilledNumberValue = (value: number | string | null | undefined): boolean =>
    value !== null &&
    value !== undefined &&
    value !== '' &&
    Number(value) > 0;

const isAddDisabled = (item: IEUAccountServices): boolean => {
    const hasRate = isFilledNumberValue(item.original_amount);
    const hasQuantity = isFilledNumberValue(item.quantity);
    const hasExchangeRate = !isOfferCurrencyDiffers(item) || isFilledNumberValue(item.exchange_rate);

    return !hasRate || !hasQuantity || !hasExchangeRate;
};
const hasTransferableServices = computed(() =>
    (form.value.services ?? []).some((item) => !isAddDisabled(item)),
);

const getTrafficPeriodPickerValue = (item: IEUAccountServices): [string, string] | null => {
    const { from, to } = trafficPeriodStringToRange(serviceToTrafficPeriodString(item, null));
    return from && to ? [from, to] : null;
};

const handleTrafficPeriodUpdate = (item: IEUAccountServices, raw: string | string[] | null): void => {
    const { from, to } = trafficPeriodStringToRange(raw);
    item.traffic_period_from = from;
    item.traffic_period_to = to;
};

const formatTrafficPeriodView = (item: IEUAccountServices): string => {
    const raw = serviceToTrafficPeriodString(item, null);
    if (!raw) return '—';
    const dates = String(raw).match(/\d{4}-\d{2}-\d{2}/g) ?? [];
    if (dates.length < 2) return String(raw);
    const toRu = (iso: string): string => {
        const p = iso.split('-');
        return p.length === 3 ? `${p[2]}.${p[1]}.${p[0]}` : iso;
    };
    return `${toRu(dates[0]!)} - ${toRu(dates[1]!)}`;
};

const ensureDefaultTrafficPeriod = (item: IEUAccountServices): void => {
    if (!item.traffic_period_from || !item.traffic_period_to) {
        const { from, to } = trafficPeriodStringToRange(getDefaultTrafficPeriodForMonth(form.value.period));
        item.traffic_period_from = from;
        item.traffic_period_to = to;
    }
};

const cloneAndRecount = (item: IEUAccountServices, uniqueId = false): IEUAccountServices => {
    const cloned = JSON.parse(JSON.stringify(item)) as IEUAccountServices;
    if (uniqueId) {
        cloned.tempId = createUniqueTempId();
    }
    // Имя услуги показываем ровно то, что пришло с бэка: пустое `service_name`
    // так и остаётся пустым. Своё название из оффера и цели не придумываем —
    // иначе оно уедет в счёт и в offers-service как выбор пользователя
    cloned.service_name = cloned.service_name ?? '';
    cloned.original_service_name = cloned.service_name;
    // Ключ обязан существовать до попадания в реактивный массив:
    // в Vue 2 присваивание нового свойства объекта не отслеживается
    cloned.unit = cloned.unit ?? null;
    ensureDefaultTrafficPeriod(cloned);
    recountServicesSum(cloned);
    return cloned;
};

const addToSelected = (item: IEUAccountServices): void => {
    if (isAddDisabled(item)) {
        return;
    }
    servicesToPay.value.push(cloneAndRecount(item, true));
};

const moveAllToSelected = (): void => {
    const transferableServices = (form.value.services ?? []).filter((item) => !isAddDisabled(item));
    if (!transferableServices.length) {
        return;
    }
    servicesToPay.value.push(...transferableServices.map((s) => cloneAndRecount(s, true)));
};

const removeFromSelected = (item: IEUAccountServices): void => {
    const index = servicesToPay.value.findIndex((service, idx) => getServiceKey(service, idx) === getServiceKey(item));
    if (index !== -1) {
        servicesToPay.value.splice(index, 1);
    }
};

const clearSelectedServices = (): void => {
    servicesToPay.value = [];
};

const handleServiceNameUpdate = (item: IEUAccountServices, value: string): void => {
    item.service_name = value;
};

const handleUnitUpdate = (item: IEUAccountServices, value: string | null): void => {
    item.unit = value || null;
};

return { __sfc: true,EUAccountsStore, form, isEdit, servicesToPay, getServicesToPaySelectedSum, getServicesToPayTargetSum, isServicesToPayDifferenceMoreThanOnePercent, isEUAccountFullyNetted, formatNumber, recountServicesSum, isSingle, unitOptions, isUnitsLoading, getServiceKey, createUniqueTempId, tableHeadersTop, tableHeadersBottom, tableConfigsTop, tableConfigsBottom, tableTopItems, tableBottomItems, hasSelectedServices, isFieldsEditable, footerRightColspan, footerLeftColspan, topTableNoDataText, isOfferCurrencyDiffers, getRateCurrency, isFilledNumberValue, isAddDisabled, hasTransferableServices, getTrafficPeriodPickerValue, handleTrafficPeriodUpdate, formatTrafficPeriodView, ensureDefaultTrafficPeriod, cloneAndRecount, addToSelected, moveAllToSelected, removeFromSelected, clearSelectedServices, handleServiceNameUpdate, handleUnitUpdate, BaseEditableCard, BaseTable, BaseButton, BaseInput, BaseInputNumber, BaseDateTimePicker, BaseQuestionMarkTooltip, BaseSelect, SvgPlus, SvgTrash, getCurrencyIcon }
}

})